import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Tabs, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { mealsPlanActions, supplementsTemplatesActions, traineeMealsPlanActions } from "@fitness-app/app-store";
import { PermissionKey } from "@fitness-app/data-models";
import { ClientNutritionStatus, type ClientMealsPlanDetails } from "@fitness-app/data-models/entities/ClientNutrition";
import {
  TargetGender,
  type DietaryExclusions,
  type MealsPlanDetails,
} from "@fitness-app/data-models/entities/MealsPlan";
import { generateTraineeMealsPlanDetails } from "@fitness-app/utils/src/nutrition/generateMealsPlanDetails";
import {
  transformAddMealPlanModelToTraineeNutrition,
  type NutrientsTargets,
  type TraineeNutritionFormModel,
  type TraineeNutritionGeneratorFormModel,
} from "@fitness-app/utils/src/nutrition/mealsPlanGenerators";

import ModalForm from "~/components/ModalForm/ModalForm";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { RevertLastChanges } from "~/modules/Nutrition/MealsPlans/MealsPlanDetails/tabs/MealsPlanSchedule/components/RevertLastChanges";
import MealsPlanSchedule from "~/modules/Nutrition/MealsPlans/MealsPlanDetails/tabs/MealsPlanSchedule/MealsPlanSchedule";
import MealsPlanShoppingList from "~/modules/Nutrition/MealsPlans/MealsPlanDetails/tabs/MealsPlanShoppingList/MealsPlanShoppingList";
import MealsPlanSupplements from "~/modules/Nutrition/MealsPlans/MealsPlanDetails/tabs/MealsPlanSupplements/MealsPlanSupplements";
import MealsPlanTargets from "~/modules/Nutrition/MealsPlans/MealsPlanDetails/tabs/MealsPlanTargets/MealsPlanTargets";
import { useGenerateNutrition } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/hooks/useGenerateNutrition";
import { NutritionPlanAgentChat } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/NutritionPlanAgentChat";
import { TraineeDishesOccurrence } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeDishesOccurrance";
import TraineeNutritionForm from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeNutritionForm";
import TraineeNutritionGeneratorForm from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeNutritionGeneratorForm";
import { TraineeNutritionMore } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeNutritionMore";
import { TraineeNutritionNotes } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeNutritionNotes";
import { traineeKeys, useTraineeSearchParams } from "~/modules/Trainee/TraineeProfile/scope";
import TraineeMealsPlanProvider from "~/shared/providers/TraineeMealsPlanProvider";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface TraineeNutritionCreatorProps {
  nutritionId: string;
  traineeId: string;
  openCurrent: (id?: string) => void;
}

const TABS_KEY = ["schedule", "targets", "shoppingList", "supplements", "attachments", "generator", "notes"];
const TAB_ICONS = {
  schedule: CalendarOutlined,
  targets: SettingOutlined,
  shoppingList: ShoppingCartOutlined,
  supplements: ExperimentOutlined,
};

export const TraineeNutritionCreator = ({
  nutritionId: assignedPlanId,
  traineeId,
  openCurrent,
}: TraineeNutritionCreatorProps): React.ReactElement => {
  const { t } = useTranslation(["nutrition", "common"]);
  const [searchParams, setSearchParams] = useTraineeSearchParams();
  const queryClient = useQueryClient();
  const tabFromUrl = searchParams.get("nutritionTab") || "";
  const tab = "nutrition";
  const activeTab = TABS_KEY.includes(tabFromUrl) ? tabFromUrl : TABS_KEY[0];
  const dispatch = useAppDispatch();
  const [showNutritionForm, toggleNutritionForm] = useState<boolean | "scheduled">(false);
  const [isNavigationCollapsed, setIsNavigationCollapsed] = useState(false);
  const [isChatVisible, setIsChatVisible] = useState(true);
  const { userId, isTeamMember, settings } = useUserRole();
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const canUseNutritionAgent = canUseAgents && (!isTeamMember || settings.nutritionGenerator !== false);
  const [generatePdfLoading, setGeneratePdfLoading] = useState(false);
  const selectedMealsPlan = useAppSelector((state) => state.traineeMealsPlan.selectedMealsPlan);
  const metadata = selectedMealsPlan?.metadata;
  const [showGeneratorForm, toggleGeneratorForm] = useState(false);
  const { generating, generateNutrition } = useGenerateNutrition(traineeId, () => toggleGeneratorForm(false));

  const similarDraftModel = useMemo<Partial<TraineeNutritionGeneratorFormModel> | null>(() => {
    if (!selectedMealsPlan) return null;

    return {
      name: selectedMealsPlan.name,
      targetCalories: selectedMealsPlan.targetCalories,
      macroSplit: selectedMealsPlan.macroSplit,
      dietaryPreference: selectedMealsPlan.dietaryPreference,
      mealsSchema: selectedMealsPlan.mealsSchema,
      targets: {
        fat: selectedMealsPlan.targets.fat,
        carbs: selectedMealsPlan.targets.carbs,
        protein: selectedMealsPlan.targets.protein,
        gender: selectedMealsPlan.targets.gender || TargetGender.UNISEX,
        activityLevel: selectedMealsPlan.targets.activityLevel,
        macroUnit: selectedMealsPlan.targets.macroUnit,
      },
      generatorVersion: "v3",
      exclusions: Array.isArray(selectedMealsPlan.metadata?.exclusions)
        ? (selectedMealsPlan.metadata?.exclusions as DietaryExclusions[])
        : [],
      prompt: String(selectedMealsPlan.metadata?.prompt || ""),
      model: selectedMealsPlan.metadata?.model as TraineeNutritionGeneratorFormModel["model"],
    };
  }, [selectedMealsPlan]);
  const [loading, onSuccess, onFailure, onStart] = useEntityChange(() => toggleNutritionForm(false));
  const [deleting, onSuccessDelete, onFailureDelete, onStartDelete] = useEntityChange(() => {
    openCurrent?.();
    void queryClient.invalidateQueries(["draftNutritionPlans"]);
  });

  useEffect(() => {
    void dispatch(supplementsTemplatesActions.fetchSupplementsTemplates());
  }, [dispatch]);

  const addNutritionToTrainee = async (
    formData: TraineeNutritionFormModel,
    targets: NutrientsTargets,
    supplementsFromCurrentPlan?: ClientMealsPlanDetails["supplements"],
  ) => {
    onStart();
    try {
      let supplements: ClientMealsPlanDetails["supplements"] = [];

      if (formData.copySupplementsPlan && supplementsFromCurrentPlan?.length) {
        supplements = supplementsFromCurrentPlan;
      }
      const modelToSave = transformAddMealPlanModelToTraineeNutrition({
        model: formData,
        nutrients: targets,
        traineeId,
        userId,
        metadata: {
          assignedBy: "trainer",
        },
      });

      let details: MealsPlanDetails | null = null;

      if (formData.selectedTemplate) {
        const response = await dispatch(
          mealsPlanActions.fetchMealsPlanWithDetails({
            id: formData.selectedTemplate,
          }),
        ).unwrap();

        details = response.details[0] || null;
      }

      if (showNutritionForm === "scheduled") {
        const { data } = await supabase.from("client_meals_plan").select("id, name").match({
          traineeId,
          status: ClientNutritionStatus.Scheduled,
          startAt: modelToSave.startAt,
        });

        if (data?.length) {
          onFailure(`Istnieje już plan rozpoczynający się w dniu ${modelToSave.startAt}`);
          return;
        }

        await dispatch(
          traineeMealsPlanActions.addScheduledNutritionToTrainee({
            nutrition: modelToSave,
            mealsPlanDetails: generateTraineeMealsPlanDetails(modelToSave, traineeId, details, supplements),
            traineeId,
          }),
        ).unwrap();
        await queryClient.invalidateQueries({
          queryKey: ["scheduledNutrition", traineeId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["archivedNutritionPlans", traineeId],
        });
        onSuccess();
      } else {
        const response = await dispatch(
          traineeMealsPlanActions.addNutritionToTrainee({
            nutrition: modelToSave,
            mealsPlanDetails: generateTraineeMealsPlanDetails(modelToSave, traineeId, details, supplements),
            shareWithClient: formData.hasActiveNutrition || false,
          }),
        ).unwrap();
        await queryClient.invalidateQueries({
          queryKey: ["archivedNutritionPlans", traineeId],
        });
        void queryClient.invalidateQueries({ queryKey: traineeKeys.all(traineeId) });
        openCurrent?.(response?.plan?.id);
        onSuccess();
      }
    } catch (e) {
      onFailure();
    }
  };

  const deleteNutrition = async () => {
    try {
      onStartDelete();
      await dispatch(
        traineeMealsPlanActions.deleteScheduledNutrition({
          mealsPlanId: assignedPlanId,
          traineeId,
          taskId: metadata?.taskId as string,
        }),
      ).unwrap();
      onSuccessDelete();
    } catch {
      onFailureDelete();
    }
  };

  const renderTabLabel = (key: keyof typeof TAB_ICONS, label: string) => {
    const Icon = TAB_ICONS[key];

    return (
      <span className="nutrition-draft-tabs__tab-label">
        <Icon />
        <span className="nutrition-draft-tabs__tab-text">{label}</span>
      </span>
    );
  };

  return (
    <TraineeMealsPlanProvider id={assignedPlanId} traineeId={traineeId}>
      <>
        <Card>
          <div className="p-6">
            <Tabs
              tabBarExtraContent={
                <div className="flex flex-col gap-y-2 p-2">
                  <Button
                    aria-label={isNavigationCollapsed ? "Rozwiń nawigację diety" : "Zwiń nawigację diety"}
                    size="small"
                    icon={isNavigationCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => setIsNavigationCollapsed((value) => !value)}
                  >
                    <span className="nutrition-draft-tabs__action-text">
                      {isNavigationCollapsed ? "Rozwiń" : "Zwiń"}
                    </span>
                  </Button>

                  {!isNavigationCollapsed ? (
                    <>
                      <RevertLastChanges />

                      <Button size="small" onClick={() => openCurrent()}>
                        Zamknij draft
                      </Button>

                      <TraineeNutritionNotes traineeId={traineeId} />

                      {canUseNutritionAgent ? (
                        <Button size="small" icon={<ExperimentOutlined />} onClick={() => toggleGeneratorForm(true)}>
                          Generuj podobny
                        </Button>
                      ) : null}

                      <TraineeNutritionMore
                        traineeId={traineeId}
                        inFuture
                        current={false}
                        assignedPlanId={assignedPlanId}
                        generatePdfLoading={generatePdfLoading}
                        setGeneratePdfLoading={setGeneratePdfLoading}
                      />

                      <Popconfirm
                        title="Czy na pewno usunąć dietę?"
                        description="Odzyskanie danych będzie niemożliwe."
                        onConfirm={deleteNutrition}
                        okText="Tak"
                        cancelText="Nie"
                        icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                        okButtonProps={{ danger: true }}
                        styles={{ root: { maxWidth: 350 } }}
                      >
                        <Button size="small" danger loading={Boolean(deleting)} icon={<DeleteOutlined />}>
                          Usuń
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </div>
              }
              tabPosition="left"
              defaultActiveKey="schedule"
              className={isNavigationCollapsed ? "tabs-reset nutrition-draft-tabs--collapsed" : "tabs-reset"}
              activeKey={activeTab}
              onChange={(nutritionTab) =>
                setSearchParams({
                  nutritionTab,
                  tab,
                  planId: assignedPlanId,
                  template: "true",
                })
              }
            >
              <Tabs.TabPane key="schedule" tab={renderTabLabel("schedule", t("mealsPlanDetails.schedule"))}>
                <div className="relative">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
                    <div className="min-w-0 flex-[2]">
                      <MealsPlanSchedule />
                      <TraineeDishesOccurrence traineeId={traineeId} planId={assignedPlanId} />
                    </div>
                    {canUseNutritionAgent && isChatVisible ? (
                      <NutritionPlanAgentChat
                        planId={assignedPlanId}
                        traineeId={traineeId}
                        onClose={() => setIsChatVisible(false)}
                      />
                    ) : null}
                  </div>

                  {canUseNutritionAgent && !isChatVisible ? (
                    <Tooltip title="Pokaż czat">
                      <Button
                        aria-label="Pokaż czat"
                        className="absolute right-3 top-3 z-10 border-gray-200 bg-white/90 text-gray-500 shadow-sm backdrop-blur transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-900"
                        icon={<MessageOutlined />}
                        shape="circle"
                        size="small"
                        onClick={() => setIsChatVisible(true)}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="targets" tab={renderTabLabel("targets", t("mealsPlanDetails.targets"))}>
                <MealsPlanTargets />
              </Tabs.TabPane>

              <Tabs.TabPane key="shoppingList" tab={renderTabLabel("shoppingList", t("mealsPlanDetails.shoppingList"))}>
                <MealsPlanShoppingList openByDefault={false} />
              </Tabs.TabPane>

              <Tabs.TabPane key="supplements" tab={renderTabLabel("supplements", t("mealsPlanDetails.supplements"))}>
                <MealsPlanSupplements />
              </Tabs.TabPane>
            </Tabs>
          </div>
        </Card>
        <ModalForm
          open={Boolean(showNutritionForm)}
          loading={!!loading}
          onCancel={() => toggleNutritionForm(false)}
          title={t("mealsPlan.addNewTitle")}
          width={850}
        >
          <TraineeNutritionForm
            traineeId={traineeId}
            onSubmit={addNutritionToTrainee}
            inFuture={showNutritionForm === "scheduled"}
          />
        </ModalForm>
        <ModalForm
          open={showGeneratorForm}
          loading={!!generating}
          onCancel={() => toggleGeneratorForm(false)}
          title="Generuj nowy jadłospis"
          okText="Generuj"
          width={850}
        >
          <TraineeNutritionGeneratorForm model={similarDraftModel} traineeId={traineeId} onSubmit={generateNutrition} />
        </ModalForm>
      </>
    </TraineeMealsPlanProvider>
  );
};
